import { FilterOutlined } from "@ant-design/icons";
import { Drawer, Flex, Select, Spin, Table, Tabs, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import type React from "react";
import { useAppDispatch } from "../../hooks/redux-hooks";
import type { ClinicianClient, ClinicianNote } from "../../service/members.service";
import { getNotesDetails } from "../../service/notes.service";
import { setNotesDetails, setRecordingId } from "../../slices/notesSlice";
import NotesView from "../notes/NotesView.component";
import TranscriptView from "../notes/TranscriptView.component";
import styles from "./clinician-profile.module.scss";

const { Text } = Typography;

interface ClinicianNotesProps {
  notes: ClinicianNote[];
  total: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  selectedClientUuid?: string;
  onClientFilterChange: (clientUuid: string | undefined) => void;
  clients: ClinicianClient[];
}

const ClinicianNotes: React.FC<ClinicianNotesProps> = ({
  notes,
  total,
  currentPage,
  onPageChange,
  loading,
  selectedClientUuid,
  onClientFilterChange,
  clients,
}) => {
  const dispatch = useAppDispatch();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [loadingNote, setLoadingNote] = useState(false);

  // Handle clicking on a note to view it
  const handleNoteClick = async (note: ClinicianNote) => {
    // Only open notes that are completed
    if (note.status !== 1) {
      message.info("This note is still processing");
      return;
    }

    setLoadingNote(true);
    setDrawerVisible(true);

    try {
      const response = await getNotesDetails(note.id);
      dispatch(setNotesDetails(response.data));
      dispatch(setRecordingId(note.id));
    } catch (error) {
      console.error("Error fetching note details:", error);
      message.error("Failed to load note details");
      setDrawerVisible(false);
    } finally {
      setLoadingNote(false);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    // Clear note details after a short delay to avoid flickering
    setTimeout(() => {
      dispatch(setNotesDetails(null));
    }, 300);
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: // DONE_PROCESSING
        return "success";
      case 0: // PROCESSING
      case 2: // UPLOADING
      case 3: // TRANSCRIBING
      case 4: // GENERATING_NOTE
        return "processing";
      case 5: // CANCELED
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: number) => {
    switch (status) {
      case 0:
        return "Processing";
      case 1:
        return "Complete";
      case 2:
        return "Uploading";
      case 3:
        return "Transcribing";
      case 4:
        return "Generating";
      case 5:
        return "Canceled";
      default:
        return "Unknown";
    }
  };

  const columns: ColumnsType<ClinicianNote> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title: string | null, record: ClinicianNote) => <Text strong>{title || `Note #${record.id}`}</Text>,
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
      render: (name: string | null) => <Text>{name || "—"}</Text>,
    },
    {
      title: "Note Type",
      dataIndex: "note_type_label",
      key: "note_type_label",
      render: (label: string) => <Text>{label}</Text>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => <Text>{new Date(date).toLocaleString()}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: number) => <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>,
    },
    {
      title: "Read",
      dataIndex: "is_read",
      key: "is_read",
      render: (isRead: boolean) => <Tag color={isRead ? "default" : "blue"}>{isRead ? "Read" : "Unread"}</Tag>,
    },
  ];

  // Build client filter options
  const clientOptions = [
    { label: "All Clients", value: undefined },
    ...clients.map((client) => ({
      label: client.name,
      value: client.uuid,
    })),
  ];

  return (
    <div>
      {/* Client Filter */}
      <Flex justify="flex-end" style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 250 }}
          placeholder="Filter by client"
          value={selectedClientUuid}
          onChange={onClientFilterChange}
          options={clientOptions}
          suffixIcon={<FilterOutlined />}
          allowClear
        />
      </Flex>

      {/* Notes Table */}
      <Table
        columns={columns}
        dataSource={notes}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          current: currentPage,
          pageSize: 50,
          showSizeChanger: false,
          onChange: onPageChange,
        }}
        className={styles.clinicianProfile__table}
        onRow={(record) => ({
          onClick: () => handleNoteClick(record),
          style: { cursor: "pointer" },
        })}
      />

      {/* Note Details Drawer */}
      <Drawer
        title="Note Details"
        placement="right"
        width="80%"
        onClose={handleCloseDrawer}
        open={drawerVisible}
        destroyOnClose
      >
        {loadingNote ? (
          <Flex justify="center" align="center" style={{ minHeight: 400 }}>
            <Spin size="large" />
          </Flex>
        ) : (
          <Tabs
            defaultActiveKey="note"
            items={[
              {
                key: "note",
                label: "Note",
                children: <NotesView />,
              },
              {
                key: "transcript",
                label: "Transcript",
                children: <TranscriptView />,
              },
            ]}
          />
        )}
      </Drawer>
    </div>
  );
};

export default ClinicianNotes;
